import {
  CheckCircle2,
  Cloud,
  CloudDownload,
  CloudUpload,
  Copy,
  ExternalLink,
  KeyRound,
  LogOut,
  RefreshCw,
  Send,
  ShieldCheck,
  UserCog,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  downloadCloudSnapshot,
  inviteBakeryOwner,
  listBakeryOwners,
  publicOrderUrl,
  publishRecipeCatalog,
  resetBakeryOwnerPassword,
  signInWithEmail,
  signOutCloud,
  signUpBaker,
  uploadCloudSnapshot,
  updateCloudPassword,
} from "../lib/cloud";
import { LOAFERS_BRAND } from "../lib/brand";
import { createBackup, parseBackup } from "../lib/storage";

function dateLabel(value) {
  if (!value) return "No cloud copy yet";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function safeSlug(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function BakerCloudPanel({
  cloudAccount,
  data,
  recipes,
  lastCloudBackupAt,
  onRestore,
  onSetLastCloudBackupAt,
}) {
  const [mode, setMode] = useState("signin");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [remoteUpdatedAt, setRemoteUpdatedAt] = useState(lastCloudBackupAt);
  const [ownerAccounts, setOwnerAccounts] = useState([]);
  const [ownerAccountsLoading, setOwnerAccountsLoading] = useState(false);
  const [ownerInvite, setOwnerInvite] = useState({ email: "", fullName: "" });
  const [ownerPassword, setOwnerPassword] = useState({
    targetUserId: "",
    password: "",
    confirmation: "",
  });
  const [invitePassword, setInvitePassword] = useState({ password: "", confirmation: "" });
  const [form, setForm] = useState({
    email: "",
    password: "",
    fullName: "",
    bakeryName: LOAFERS_BRAND.businessName,
    slug: "loafers",
  });
  const backup = useMemo(() => createBackup(data), [data]);
  const workspace = cloudAccount.workspace;
  const orderLink = publicOrderUrl(workspace?.bakery?.slug || "loafers");
  const primaryOwner = Boolean(
    workspace?.bakery?.owner_id
      && workspace.bakery.owner_id === cloudAccount.session?.user?.id,
  );
  const otherOwners = ownerAccounts.filter((owner) => !owner.isPrimary);
  const inviteSetup = new URLSearchParams(window.location.search).get("owner-invite") === "1";

  useEffect(() => {
    if (!workspace?.bakeryId) return;
    downloadCloudSnapshot(workspace.bakeryId)
      .then((snapshot) => setRemoteUpdatedAt(snapshot?.updated_at || null))
      .catch(() => {});
  }, [workspace?.bakeryId]);

  useEffect(() => {
    let active = true;
    if (!primaryOwner || !workspace?.bakeryId) {
      setOwnerAccounts([]);
      setOwnerPassword({ targetUserId: "", password: "", confirmation: "" });
      return undefined;
    }

    setOwnerAccountsLoading(true);
    listBakeryOwners(workspace.bakeryId)
      .then((members) => {
        if (!active) return;
        setOwnerAccounts(members);
        const firstOtherOwner = members.find((member) => !member.isPrimary);
        setOwnerPassword((current) => ({
          ...current,
          targetUserId: current.targetUserId || firstOtherOwner?.userId || "",
        }));
      })
      .catch((nextError) => {
        if (active) setError(nextError.message);
      })
      .finally(() => {
        if (active) setOwnerAccountsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [primaryOwner, workspace?.bakeryId]);

  async function submitAccount(event) {
    event.preventDefault();
    setBusy("account");
    setError("");
    setMessage("");
    try {
      if (mode === "signin") {
        await signInWithEmail(form.email.trim(), form.password);
        setMessage("Signed in. Your bakery cloud is ready.");
      } else {
        const result = await signUpBaker({
          email: form.email.trim(),
          password: form.password,
          fullName: form.fullName.trim(),
          bakeryName: form.bakeryName.trim(),
          slug: safeSlug(form.slug || form.bakeryName),
        });
        if (result.session) {
          await cloudAccount.refreshWorkspace(result.session);
          setMessage("Bakery account created.");
        } else {
          setMessage("Account created. Check your email to confirm it, then sign in.");
          setMode("signin");
        }
      }
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function uploadSnapshot() {
    setBusy("upload");
    setError("");
    setMessage("");
    try {
      const result = await uploadCloudSnapshot(
        workspace.bakeryId,
        backup,
        cloudAccount.session.user.id,
      );
      setRemoteUpdatedAt(result.updated_at);
      onSetLastCloudBackupAt(result.updated_at);
      setMessage("This device is safely copied to the cloud.");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function restoreSnapshot() {
    if (!window.confirm("Replace this device's bakery records with the latest cloud copy? A recovery copy will be kept.")) return;
    setBusy("restore");
    setError("");
    setMessage("");
    try {
      const remote = await downloadCloudSnapshot(workspace.bakeryId);
      if (!remote?.data) throw new Error("There is no cloud copy to restore yet.");
      const parsed = parseBackup(JSON.stringify(remote.data));
      onRestore(parsed.data, backup);
      setMessage("Cloud copy restored. You can undo this from the recovery section below.");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function publishProducts() {
    setBusy("publish");
    setError("");
    setMessage("");
    try {
      const count = await publishRecipeCatalog(workspace.bakeryId, recipes);
      setMessage(`${count} recipe${count === 1 ? "" : "s"} published to your customer menu.`);
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function copyOrderLink() {
    try {
      await navigator.clipboard.writeText(orderLink);
      setMessage("Customer order link copied.");
    } catch {
      setError("Copying was blocked. Open the order page and copy its address instead.");
    }
  }

  async function signOut() {
    setBusy("signout");
    try {
      await signOutCloud();
      setMessage("Signed out.");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function changeOwnerPassword(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!ownerPassword.targetUserId) {
      setError("Choose another owner account first.");
      return;
    }
    if (ownerPassword.password !== ownerPassword.confirmation) {
      setError("The two new-password entries do not match.");
      return;
    }

    setBusy("owner-password");
    try {
      const result = await resetBakeryOwnerPassword({
        bakeryId: workspace.bakeryId,
        targetUserId: ownerPassword.targetUserId,
        password: ownerPassword.password,
      });
      setOwnerPassword((current) => ({
        ...current,
        password: "",
        confirmation: "",
      }));
      setMessage(`Password changed for ${result.email}. They can sign in with the new password now.`);
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function sendOwnerInvite(event) {
    event.preventDefault();
    setBusy("owner-invite");
    setError("");
    setMessage("");
    try {
      const result = await inviteBakeryOwner({
        bakeryId: workspace.bakeryId,
        email: ownerInvite.email.trim(),
        fullName: ownerInvite.fullName.trim(),
      });
      const members = await listBakeryOwners(workspace.bakeryId);
      setOwnerAccounts(members);
      const invited = members.find((member) => member.userId === result.userId);
      setOwnerPassword((current) => ({ ...current, targetUserId: invited?.userId || current.targetUserId }));
      setOwnerInvite({ email: "", fullName: "" });
      setMessage(result.invitationSent
        ? `Invitation sent to ${result.email}. The link opens the secure password setup screen.`
        : result.needsPassword
          ? `${result.email} is now linked. Select that owner below and set a password to finish access.`
          : `${result.email} already had an account and is now linked. They can sign in with their existing password.`);
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  async function finishOwnerInvitation(event) {
    event.preventDefault();
    setError("");
    setMessage("");
    if (invitePassword.password !== invitePassword.confirmation) {
      setError("The two password entries do not match.");
      return;
    }
    if (invitePassword.password.length < 12
      || !/[a-z]/.test(invitePassword.password)
      || !/[A-Z]/.test(invitePassword.password)
      || !/[0-9]/.test(invitePassword.password)
      || !/[^A-Za-z0-9]/.test(invitePassword.password)) {
      setError("Use 12+ characters with uppercase, lowercase, a number, and a symbol.");
      return;
    }
    setBusy("invite-password");
    try {
      await updateCloudPassword(invitePassword.password);
      setInvitePassword({ password: "", confirmation: "" });
      const url = new URL(window.location.href);
      url.searchParams.delete("owner-invite");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      setMessage("Owner setup complete. You can now sign in on any device with this email and password.");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setBusy("");
    }
  }

  if (!cloudAccount.configured) {
    return (
      <section className="storage-section cloud-section">
        <div className="section-title-line">
          <div><span className="eyebrow-label dark">Prepared</span><h3>Cloud & customer orders</h3></div>
          <Cloud size={19} />
        </div>
        <div className="cloud-ready-card">
          <ShieldCheck size={24} />
          <div>
            <strong>The secure cloud layer is ready</strong>
            <p>Connect a Supabase project to turn on baker accounts, cloud copies, customer accounts, and live order requests.</p>
          </div>
        </div>
        <ul className="cloud-checklist">
          <li><CheckCircle2 /> Private bakery records protected by account access</li>
          <li><CheckCircle2 /> Guest and signed-in customer order requests</li>
          <li><CheckCircle2 /> Shareable menu built from your recipes and prices</li>
        </ul>
        <a className="storage-file-button" href={orderLink}>
          <ExternalLink size={16} /> Preview customer order page
        </a>
        <small className="cloud-footnote">Cloud remains off until the project connection is added.</small>
      </section>
    );
  }

  if (cloudAccount.loading) {
    return (
      <section className="storage-section cloud-section cloud-loading">
        <RefreshCw className="spin" size={18} /> Checking cloud account…
      </section>
    );
  }

  if (!cloudAccount.session) {
    return (
      <section className="storage-section cloud-section">
        <div className="section-title-line">
          <div><span className="eyebrow-label dark">Secure account</span><h3>{mode === "signin" ? "Sign in to cloud" : "Create bakery account"}</h3></div>
          <UserRound size={19} />
        </div>
        <div className="cloud-mode-switch">
          <button type="button" className={mode === "signin" ? "selected" : ""} onClick={() => setMode("signin")}>Sign in</button>
          <button type="button" className={mode === "signup" ? "selected" : ""} onClick={() => setMode("signup")}>Create account</button>
        </div>
        <form className="form-stack" onSubmit={submitAccount}>
          {mode === "signup" ? (
            <>
              <label>Your name<input required value={form.fullName} onChange={(event) => setForm({ ...form, fullName: event.target.value })} /></label>
              <label>Bakery name<input required value={form.bakeryName} onChange={(event) => setForm({ ...form, bakeryName: event.target.value, slug: safeSlug(event.target.value) })} /></label>
              <label>Order page name<div className="slug-field"><span>?order=</span><input required value={form.slug} onChange={(event) => setForm({ ...form, slug: safeSlug(event.target.value) })} /></div></label>
            </>
          ) : null}
          <label>Email<input required type="email" autoComplete="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
          <label>Password<input required minLength="8" type="password" autoComplete={mode === "signin" ? "current-password" : "new-password"} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>
          <button className="primary-button" type="submit" disabled={busy === "account"}>
            {busy === "account" ? "Please wait…" : mode === "signin" ? "Sign in" : "Create secure account"}
          </button>
        </form>
        {error || cloudAccount.error ? <p className="form-error" role="alert">{error || cloudAccount.error}</p> : null}
        {message ? <div className="storage-success"><CheckCircle2 size={17} /> {message}</div> : null}
      </section>
    );
  }

  if (!workspace) {
    return (
      <section className="storage-section cloud-section">
        <div className="cloud-ready-card">
          <UserRound size={22} />
          <div><strong>{cloudAccount.session.user.email}</strong><p>{inviteSetup ? "The invitation is signed in, but its bakery link is not ready. Ask the primary owner to resend the invitation." : "This is a customer account. Create or sign in with a bakery-owner account to manage cloud records."}</p></div>
        </div>
        <button className="storage-file-button" type="button" onClick={signOut}><LogOut size={16} /> Sign out</button>
      </section>
    );
  }

  return (
    <section className="storage-section cloud-section cloud-connected">
      <div className="section-title-line">
        <div><span className="eyebrow-label dark">Connected</span><h3>{workspace.bakery.name}</h3></div>
        <Cloud size={19} />
      </div>
      <div className="cloud-account-line">
        <span><strong>{cloudAccount.session.user.email}</strong><small>{workspace.role} · last cloud copy {dateLabel(remoteUpdatedAt)}</small></span>
        <button type="button" onClick={signOut} aria-label="Sign out"><LogOut size={16} /></button>
      </div>
      <div className="owner-security-grid">
        <article>
          <ShieldCheck size={17} />
          <span><strong>Owner-only area</strong><small>{workspace.role === "owner" ? "Signed in as owner" : `Signed in as ${workspace.role}`}</small></span>
        </article>
        <article>
          <KeyRound size={17} />
          <span><strong>Safer cloud data</strong><small>Private records require this bakery account</small></span>
        </article>
        <article>
          <Cloud size={17} />
          <span><strong>Multi-device sync</strong><small>Copy here, restore on the next device</small></span>
        </article>
      </div>
      <div className="cloud-action-grid">
        <button type="button" onClick={uploadSnapshot} disabled={Boolean(busy)}>
          <CloudUpload size={19} /><span><strong>Copy to cloud</strong><small>Upload this device</small></span>
        </button>
        <button type="button" onClick={restoreSnapshot} disabled={Boolean(busy)}>
          <CloudDownload size={19} /><span><strong>Restore cloud copy</strong><small>Replace this device</small></span>
        </button>
      </div>
      {inviteSetup ? (
        <form className="owner-password-manager owner-invite-finish" onSubmit={finishOwnerInvitation}>
          <div className="owner-password-heading">
            <KeyRound size={19} />
            <span><strong>Finish your owner invitation</strong><small>Create the password you will use to sign in to BakeOS.</small></span>
          </div>
          <div className="owner-password-fields">
            <label>New password<input required minLength="12" maxLength="128" type="password" autoComplete="new-password" value={invitePassword.password} onChange={(event) => setInvitePassword({ ...invitePassword, password: event.target.value })} /></label>
            <label>Confirm password<input required minLength="12" maxLength="128" type="password" autoComplete="new-password" value={invitePassword.confirmation} onChange={(event) => setInvitePassword({ ...invitePassword, confirmation: event.target.value })} /></label>
          </div>
          <small className="owner-password-policy">12+ characters with uppercase, lowercase, a number, and a symbol.</small>
          <button className="storage-file-button" type="submit" disabled={Boolean(busy)}><ShieldCheck size={16} /> {busy === "invite-password" ? "Saving password…" : "Complete owner setup"}</button>
        </form>
      ) : null}
      {primaryOwner ? (
        <div className="owner-password-manager">
          <div className="owner-password-heading">
            <UserCog size={19} />
            <span>
              <strong>Other owner passwords</strong>
              <small>Primary-owner control. Existing passwords are never shown.</small>
            </span>
          </div>
          <form className="owner-password-form owner-invite-form" onSubmit={sendOwnerInvite}>
            <div className="owner-password-fields">
              <label>Owner name<input value={ownerInvite.fullName} onChange={(event) => setOwnerInvite({ ...ownerInvite, fullName: event.target.value })} placeholder="Name" /></label>
              <label>Email<input required type="email" autoComplete="email" value={ownerInvite.email} onChange={(event) => setOwnerInvite({ ...ownerInvite, email: event.target.value })} placeholder="owner@example.com" /></label>
            </div>
            <button className="storage-file-button" type="submit" disabled={Boolean(busy)}><Send size={16} /> {busy === "owner-invite" ? "Sending invitation…" : "Invite another owner"}</button>
          </form>
          {ownerAccountsLoading ? (
            <p className="owner-password-empty"><RefreshCw className="spin" size={15} /> Loading owner accounts…</p>
          ) : otherOwners.length ? (
            <form className="owner-password-form" onSubmit={changeOwnerPassword}>
              <label>
                Owner account
                <select
                  value={ownerPassword.targetUserId}
                  onChange={(event) => setOwnerPassword({ ...ownerPassword, targetUserId: event.target.value })}
                >
                  {otherOwners.map((owner) => (
                    <option key={owner.userId} value={owner.userId}>{owner.email}{owner.confirmedAt ? "" : " — invite pending"}</option>
                  ))}
                </select>
              </label>
              <div className="owner-password-fields">
                <label>
                  New password
                  <input
                    required
                    minLength="12"
                    maxLength="128"
                    type="password"
                    autoComplete="new-password"
                    value={ownerPassword.password}
                    onChange={(event) => setOwnerPassword({ ...ownerPassword, password: event.target.value })}
                  />
                </label>
                <label>
                  Confirm password
                  <input
                    required
                    minLength="12"
                    maxLength="128"
                    type="password"
                    autoComplete="new-password"
                    value={ownerPassword.confirmation}
                    onChange={(event) => setOwnerPassword({ ...ownerPassword, confirmation: event.target.value })}
                  />
                </label>
              </div>
              <small className="owner-password-policy">12+ characters with uppercase, lowercase, a number, and a symbol.</small>
              <button className="storage-file-button" type="submit" disabled={Boolean(busy)}>
                <KeyRound size={16} /> {busy === "owner-password" ? "Changing password…" : "Set owner password"}
              </button>
            </form>
          ) : (
            <p className="owner-password-empty">No additional owners yet. Send an invitation above.</p>
          )}
        </div>
      ) : null}
      <div className="customer-link-card">
        <div><span className="eyebrow-label dark">Customer ordering</span><strong>/{workspace.bakery.slug}</strong></div>
        <p>Publish your current recipes, then share this link with customers.</p>
        <button className="primary-button" type="button" onClick={publishProducts} disabled={Boolean(busy)}>
          <Send size={16} /> {busy === "publish" ? "Publishing…" : "Publish recipe menu"}
        </button>
        <div className="customer-link-actions">
          <button type="button" onClick={copyOrderLink}><Copy size={15} /> Copy link</button>
          <a href={orderLink}><ExternalLink size={15} /> Open page</a>
        </div>
      </div>
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {message ? <div className="storage-success"><CheckCircle2 size={17} /> {message}</div> : null}
    </section>
  );
}
